import React, { useEffect, useState } from "react";
import { LazyLoadComponent } from "react-lazy-load-image-component";
import classes from "./Scholarships.module.scss";
import CustomImage from "../../CustomImage";
import useIsMobile from "../../../hooks/useIsMobile";
import useEmblaCarousel from "embla-carousel-react";
import { Card } from "react-bootstrap";
import { isFrappeOrJotFormClass } from "../../../utils/helpers";
import DollarEnclosed from "../../UI/Icons/DollarEnclosed";
import FeatherNote from "../../UI/Icons/FeatherNote";

const ScholarshipCard = ({ scholarship, cardClassName, jotformId }: { scholarship: any; cardClassName: string; jotformId: string }) => (
  <Card className={classes[cardClassName]}>
    <div className={classes.cardImageWrap}>
      <LazyLoadComponent>
        <img
          src={scholarship?.image?.url ?? "/images/university-detail-gallery/university-dummy-logo.jpg"}
          alt={scholarship?.image?.name ?? ""}
          className={classes.cardImage}
          loading="lazy"
          decoding="async"
        />
      </LazyLoadComponent>
    </div>

    <div className={classes["card-content"]}>
      {scholarship?.subTitle && <p className={classes["university-title"]}>{scholarship.subTitle}</p>}
      {scholarship?.title && <h3 className={classes["scholarship-title"]}>{scholarship.title}</h3>}
      {scholarship?.amount && (
        <div className={classes["scholarship-amount"]}>
          <DollarEnclosed width={19} height={19} />
          <p className={classes["text-amount"]}>
            <span>Amount : </span> {scholarship.amount}
          </p>
        </div>
      )}
      {scholarship?.type && (
        <div className={classes["scholarship-type"]}>
          <FeatherNote width={19} height={19} />
          <p className={classes["text-type"]}>
            Scholarship Type : <span>{scholarship.type}</span>
          </p>
        </div>
      )}
      {scholarship.description && <p className={classes["scholarship-description"]}>{scholarship.description}</p>}
    </div>

    <button type="button" className={`${classes["enquiry-btn"]} ${isFrappeOrJotFormClass(jotformId)}`}>
      Apply Now
    </button>
  </Card>
);

const Scholarships = ({ countryData, scholarshipsData }: { countryData: any; scholarshipsData: any }) => {
  const isMobile = useIsMobile(768);
  const [scholarships, setScholarships] = useState<any>([]);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, align: "start" });

  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const scholarshipSectionData = countryData?.scholarships;

  useEffect(() => {
    setScholarships(scholarshipsData?.scholarshipsPrograms?.popularCountryPrograms ?? []);
  }, [countryData, scholarshipsData]);

  useEffect(() => {
    if (!emblaApi) return;
    const updateScrollState = () => {
      setCanScrollPrev(emblaApi.canScrollPrev());
      setCanScrollNext(emblaApi.canScrollNext());
    };
    updateScrollState();
    emblaApi.on("select", updateScrollState);
    emblaApi.on("reInit", updateScrollState);
    return () => {
      emblaApi.off("select", updateScrollState);
      emblaApi.off("reInit", updateScrollState);
    };
  }, [emblaApi, scholarships]);

  const goToPrev = () => emblaApi?.scrollPrev();
  const goToNext = () => emblaApi?.scrollNext();

  const viewMoreClickHandler = () => {
    window.open(`/student-services/study-abroad-scholarships/${countryData?.slug?.replace("study-in-", "")}`, "_blank");
  };

  return (
    <div id="scholarships" className={classes["scholarships-container"]}>
      <h2 className={classes["scholarships-title"]}>{scholarshipSectionData?.title ?? ""}</h2>
      <div className={classes["scholarships-description"]} dangerouslySetInnerHTML={{ __html: scholarshipSectionData?.description ?? "" }}></div>

      {isMobile ? (
        // Mobile View
        <div className={classes["scholarships-cards-container"]}>
          {/* EMBLA CAROUSEL */}
          <div className={classes.viewport} ref={emblaRef}>
            <div className={classes["scholarships-cards-slider-container"]}>
              {scholarships.map((scholarship: any) => (
                <div className={classes.slide} key={scholarship?.id}>
                  <ScholarshipCard
                    scholarship={scholarship}
                    cardClassName="bootstrap-card-mobile"
                    jotformId={scholarshipsData?.meta?.jotforms?.[0]?.jotformid}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className={classes["arrows-container"]}>
            {/* LEFT BUTTON */}
            <div
              className={`${classes["btcircle"]}${canScrollPrev ? "" : ` ${classes["btcircle-disabled"]}`}`}
              onClick={canScrollPrev ? goToPrev : undefined}
              aria-disabled={!canScrollPrev}
            >
              <CustomImage src="/new-images/left-arrow.svg" height={17} width={10} alt={"left-arrow"} />
            </div>

            {/* RIGHT BUTTON */}
            <div
              className={`${classes["btcircle"]}${canScrollNext ? "" : ` ${classes["btcircle-disabled"]}`}`}
              onClick={canScrollNext ? goToNext : undefined}
              aria-disabled={!canScrollNext}
            >
              <CustomImage src="/new-images/right-arrow.svg" height={17} width={10} alt={"right-arrow"} />
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className={classes["scholarships-cards-container"]}>
            {/* EMBLA CAROUSEL */}
            <div className={classes.viewport} ref={emblaRef}>
              <div className={classes["scholarships-cards-slider-container"]}>
                {scholarships.map((scholarship: any) => (
                  <div className={classes.slide} key={scholarship?.id}>
                    <ScholarshipCard scholarship={scholarship} cardClassName="bootstrap-card" jotformId={scholarshipsData?.meta?.jotforms?.[0]?.jotformid} />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className={classes["arrows-container"]}>
            {/* LEFT BUTTON */}
            <div
              className={`${classes["btcircle"]}${canScrollPrev ? "" : ` ${classes["btcircle-disabled"]}`}`}
              onClick={canScrollPrev ? goToPrev : undefined}
              aria-disabled={!canScrollPrev}
            >
              <CustomImage src="/new-images/left-arrow.svg" height={17} width={10} alt={"left-arrow"} />
            </div>
            {/* RIGHT BUTTON */}
            <div
              className={`${classes["btcircle"]}${canScrollNext ? "" : ` ${classes["btcircle-disabled"]}`}`}
              onClick={canScrollNext ? goToNext : undefined}
              aria-disabled={!canScrollNext}
            >
              <CustomImage src="/new-images/right-arrow.svg" height={17} width={10} alt={"right-arrow"} />
            </div>
          </div>
        </>
      )}

      <button onClick={viewMoreClickHandler} className={classes["view-more-scholarships-btn"]}>
        View More Scholarships
      </button>
    </div>
  );
};

export default Scholarships;
